import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/hooks/use-toast";
import { trackEvent } from "@/lib/analytics";
import { logSiteEvent } from "@/lib/site-events";
import { CheckCircle, FileText } from "lucide-react";

interface FreeGuideSignupProps {
  /** Where the request came from — recorded on the subscriber row for retargeting. */
  source?: string;
  className?: string;
}

const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Free lead magnet: emails the fillable Emergency Info Sheet and, with the
 * visitor's consent, starts the double opt-in newsletter flow.
 */
const FreeGuideSignup = ({ source = "free_guide", className = "" }: FreeGuideSignupProps) => {
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [subscribe, setSubscribe] = useState(true);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const clean = email.trim().toLowerCase();
    if (!emailRe.test(clean) || clean.length > 255) {
      toast({ title: "Please enter a valid email address.", variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("send-free-guide", {
        body: { email: clean, firstName: firstName.trim() || undefined, source, subscribe },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      setDone(true);
      trackEvent("lead_magnet_download", { magnet: "emergency_info_sheet", source });
      logSiteEvent("newsletter_signup", undefined, source);
      toast({
        title: "On its way",
        description: "Check your inbox for the Emergency Info Sheet.",
      });
    } catch (err) {
      toast({
        title: "We couldn't send it",
        description: err instanceof Error ? err.message : "Please try again in a moment.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`rounded-2xl border border-primary/20 bg-accent p-7 md:p-9 ${className}`}>
      <p className="text-xs uppercase tracking-[0.25em] text-primary mb-3 font-medium">Free download</p>
      <h3 className="text-xl md:text-2xl font-heading font-semibold mb-3 flex items-start gap-3">
        <FileText className="h-6 w-6 text-primary shrink-0 mt-0.5" aria-hidden="true" />
        The Emergency Info Sheet
      </h3>
      <p className="text-foreground/80 leading-relaxed mb-6">
        One fillable page holding medications, providers, allergies, insurance, and emergency contacts — the
        thing you will wish you had at 2am in an emergency room. It's a bonus from our book, and it's free.
      </p>

      {done ? (
        <div className="flex items-start gap-3 text-sm text-foreground/80">
          <CheckCircle className="h-5 w-5 shrink-0 mt-0.5 text-primary" aria-hidden="true" />
          <p>
            Sent. Check your inbox — the download link stays active for 48 hours. If it doesn't arrive within
            a few minutes, look in your spam folder or email hello@kinshipcarecollective.com.
          </p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-3" aria-label="Free Emergency Info Sheet">
          <div className="flex flex-col sm:flex-row gap-2">
            <label htmlFor={`guide-name-${source}`} className="sr-only">
              First name (optional)
            </label>
            <Input
              id={`guide-name-${source}`}
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="First name (optional)"
              maxLength={100}
              autoComplete="given-name"
            />
            <label htmlFor={`guide-email-${source}`} className="sr-only">
              Email address
            </label>
            <Input
              id={`guide-email-${source}`}
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              maxLength={255}
              autoComplete="email"
            />
            <Button type="submit" disabled={loading} className="shrink-0">
              {loading ? "Sending…" : "Email it to me"}
            </Button>
          </div>
          <label className="flex items-start gap-2 text-xs text-muted-foreground cursor-pointer">
            <input
              type="checkbox"
              checked={subscribe}
              onChange={(e) => setSubscribe(e.target.checked)}
              className="mt-0.5 accent-current"
            />
            <span>
              Also send me occasional caregiver resources and new guides. No selling, no spam — unsubscribe
              anytime.
            </span>
          </label>
        </form>
      )}
    </div>
  );
};

export default FreeGuideSignup;
